import type { App } from "vue";
import { computed, ref } from "vue";
import type { TolgeeInstance } from "@tolgee/vue";
import { ENV } from "@/ENV";
import {
  type LinguaCatalogRuntime,
  type OccurrenceMetadata,
} from "@fi-solutions/lingua-runtime-vue";
import {
  changeTolgeeLanguage,
  createTolgeeRuntime,
  getAppTranslationsByPrefix as getTolgeeTranslationsByPrefix,
  hasAppTranslation as hasTolgeeTranslation,
  provideTolgeeContext,
  synchronizeTolgeeLanguage,
  tolgeeRevision,
  translateApp as translateTolgeeApp,
  useAppTranslate as useTolgeeAppTranslate,
  useBdeTranslate as useTolgeeBdeTranslate,
} from "@/tolgee";
import {
  activateLinguaNamespace,
  APP_NAMESPACE,
  BDE_NAMESPACE,
  changeLinguaLanguage,
  createLinguaRuntime,
  getLinguaMessage,
  getLinguaOccurrence,
  getLinguaTranslationsByPrefix,
  hasLinguaTranslation,
  linguaRevision,
  translateLingua,
} from "@/lingua";

export type StaticTranslationProvider = "tolgee" | "lingua";
type StaticRuntime = TolgeeInstance | LinguaCatalogRuntime;

const providerValue: StaticTranslationProvider =
  ENV?.VITE_APP_TRANSLATION_PROVIDER?.toLowerCase() === "lingua" ? "lingua" : "tolgee";
export const staticTranslationProvider = ref<StaticTranslationProvider>(providerValue);
export const staticTranslationRevision = computed(() =>
  staticTranslationProvider.value === "lingua" ? linguaRevision.value : tolgeeRevision.value,
);

export async function createStaticTranslationRuntime(
  initialLanguage?: string,
): Promise<StaticRuntime> {
  if (staticTranslationProvider.value === "lingua") return createLinguaRuntime(initialLanguage);
  const runtime = await createTolgeeRuntime(initialLanguage);
  return runtime;
}

export function provideStaticTranslationContext(app: App, runtime: StaticRuntime): void {
  if (staticTranslationProvider.value === "tolgee") {
    provideTolgeeContext(app, runtime as TolgeeInstance);
  }
}

export function synchronizeStaticTranslationLanguage(
  changeApplicationLanguage: (language: string) => Promise<unknown>,
): void {
  if (staticTranslationProvider.value === "tolgee") {
    synchronizeTolgeeLanguage(changeApplicationLanguage);
  }
}

export function changeStaticTranslationLanguage(language: string): Promise<unknown> {
  return staticTranslationProvider.value === "lingua"
    ? changeLinguaLanguage(language)
    : changeTolgeeLanguage(language);
}

export function translateStaticApp(key: string, ...arguments_: unknown[]): string {
  return staticTranslationProvider.value === "lingua"
    ? translateLingua(APP_NAMESPACE, key, ...arguments_)
    : translateTolgeeApp(key, ...arguments_);
}

export function hasStaticAppTranslation(key: string, language?: string): boolean {
  if (staticTranslationProvider.value === "tolgee") return hasTolgeeTranslation(key, language);
  return hasLinguaTranslation(APP_NAMESPACE, key);
}

export function getStaticAppTranslationsByPrefix(
  prefix: string,
  language?: string,
): Record<string, string> {
  if (staticTranslationProvider.value === "tolgee") {
    return getTolgeeTranslationsByPrefix(prefix, language);
  }
  return getLinguaTranslationsByPrefix(APP_NAMESPACE, prefix);
}

export function staticMessage(namespace: string, key: string): string | undefined {
  return staticTranslationProvider.value === "lingua"
    ? getLinguaMessage(namespace, key)
    : undefined;
}

export function translateStaticMessage(namespace: string, key: string): string {
  return staticTranslationProvider.value === "lingua"
    ? translateLingua(namespace, key)
    : translateTolgeeApp(key);
}

export function staticOccurrence(namespace: string, key: string): OccurrenceMetadata | undefined {
  return staticTranslationProvider.value === "lingua"
    ? getLinguaOccurrence(namespace, key)
    : undefined;
}

function useLinguaTranslate(namespace: string) {
  const isLoading = ref(namespace !== APP_NAMESPACE);
  if (namespace !== APP_NAMESPACE) {
    void activateLinguaNamespace(namespace)
      .catch((error) => {
        // The namespace loader already attempts the immutable bundled fallback.
        // eslint-disable-next-line no-console
        console.error(`Failed to activate Lingua namespace ${namespace}`, error);
      })
      .finally(() => {
        isLoading.value = false;
      });
  }
  return {
    t: (key: string, ...arguments_: unknown[]) => {
      void linguaRevision.value;
      return translateLingua(namespace, key, ...arguments_);
    },
    isLoading,
  };
}

export function useAppTranslate() {
  return staticTranslationProvider.value === "lingua"
    ? useLinguaTranslate(APP_NAMESPACE)
    : useTolgeeAppTranslate();
}

export function useBdeTranslate() {
  return staticTranslationProvider.value === "lingua"
    ? useLinguaTranslate(BDE_NAMESPACE)
    : useTolgeeBdeTranslate();
}

export type AppTranslate = ReturnType<typeof useAppTranslate>["t"];
export type BdeTranslate = ReturnType<typeof useBdeTranslate>["t"];
